import React, { useEffect, useState } from "react";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Loader2, Star } from "lucide-react";

// Colore in base al voto medio (1-5).
const scoreColor = (v) =>
  v >= 4 ? "text-emerald-700" : v >= 3 ? "text-brand-gold-deep" : "text-red-600";

function AvgBar({ value }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-full bg-brand-line overflow-hidden">
        <div className="h-full bg-brand-blue" style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-sm font-semibold ${scoreColor(value)}`}>{value.toFixed(1)}</span>
    </div>
  );
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({}); // course_id -> commenti espansi

  useEffect(() => {
    api.get("/admin/feedback")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-feedback" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <MessageSquare size={14} /> Pannello Super Admin
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-1">Feedback corsi</h1>
          <p className="text-sm text-brand-muted">Gradimento raccolto dagli studenti, in forma anonima, corso per corso.</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-brand-muted py-12 justify-center">
            <Loader2 className="animate-spin" size={18} /> Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-brand-line rounded-sm p-10 text-center text-brand-muted text-sm">
            <Star size={22} className="inline mb-2 opacity-60" />
            <div>Nessun feedback ancora raccolto.</div>
            <div className="text-xs mt-1">I questionari compaiono quando gli studenti li compilano a fine corso.</div>
          </div>
        ) : (
          <div className="space-y-5">
            {items.map((c) => {
              const overall = c.averages.length ? c.averages.reduce((a, b) => a + b, 0) / c.averages.length : 0;
              return (
                <div key={c.course_id} className="bg-white border border-brand-line rounded-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-5 py-3 bg-brand-bg border-b border-brand-line">
                    <div className="font-semibold text-brand-ink truncate">{c.course_title}</div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-brand-muted">{c.responses} rispost{c.responses === 1 ? "a" : "e"}</span>
                      <span className={`text-sm font-semibold ${scoreColor(overall)}`}>media {overall.toFixed(1)}/5</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 space-y-2.5">
                    {c.questions.map((q, i) => (
                      <div key={i} className="flex items-center justify-between gap-4">
                        <span className="text-sm text-brand-ink">{i + 1}. {q}</span>
                        <AvgBar value={c.averages[i] ?? 0} />
                      </div>
                    ))}
                  </div>
                  {c.comments.length > 0 && (
                    <div className="border-t border-brand-line px-5 py-3">
                      <button
                        onClick={() => setOpen((o) => ({ ...o, [c.course_id]: !o[c.course_id] }))}
                        className="text-sm font-semibold text-brand-blue hover:underline"
                      >
                        {open[c.course_id] ? "Nascondi" : "Mostra"} commenti ({c.comments.length})
                      </button>
                      {open[c.course_id] && (
                        <ul className="mt-3 space-y-2">
                          {c.comments.map((cm, i) => (
                            <li key={i} className="text-sm text-brand-ink bg-brand-bg rounded-sm px-3 py-2 border border-brand-line">
                              “{cm}”
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
